import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Field from "../../../DesignSystem/Inputs/Field";
import CheckboxMultiSelect from "../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import AlertBanner from "../../../DesignSystem/AlertBanner/AlertBanner";
import { db } from "../../../../backend/firebaseConfig";
import { subscribeAdviserFullDetailsByEmailOrId } from "../../../../context/Adviser/Utils/AdviserLiveDetails";
import { getIODetailsRef } from "../IODetails/ioDetailsService";
import { getAdviserRatesRef } from "./ratesService";
import {
  getSavedGroupDetails,
  saveGroupBankDetailsByIOGroupIds,
} from "../../../GroupDetails/groupDetailsService";
import {
  isValidStatementEmails,
  normaliseStatementEmails,
  statementRecipientEmails,
} from "./statementRecipients";
import { PostCard, Row } from "../HelperFunctions/HelperFunctions";
import "../../../../css/Adviser/EditAdviserDetailsPage.css";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];
const DEFAULT_STATEMENT_EMAIL = "accounts@rosemountfs.co.uk";

const toIdList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

const hasSharedGroupId = (currentGroupIds = [], adviserGroupIds = []) => {
  if (!currentGroupIds.length) return false;
  const adviserGroupSet = new Set(
    adviserGroupIds.map((groupId) => groupId.toLowerCase()),
  );
  return currentGroupIds.some((groupId) =>
    adviserGroupSet.has(groupId.toLowerCase()),
  );
};

const formatSortCode = (value = "") =>
  String(value)
    .replace(/\D/g, "")
    .slice(0, 6)
    .replace(/(\d{2})(?=\d)/g, "$1-");

const formatAccountNumber = (value = "") =>
  String(value).replace(/\D/g, "").slice(0, 8);

const validateBankDetails = (form = {}) => {
  if (form.arSortCode && !/^\d{2}-\d{2}-\d{2}$/.test(form.arSortCode)) {
    throw new Error("Sort Code must be in 00-00-00 format.");
  }

  if (form.arAccountNumber && !/^\d{8}$/.test(form.arAccountNumber)) {
    throw new Error("Account Number must be 8 digits.");
  }
};

export default function EditGroupBankDetailsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const adviserKey = decodeURIComponent(params.adviserKey || "");
  const [details, setDetails] = useState(state?.details || null);
  const [groupLoading, setGroupLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [groupAdvisers, setGroupAdvisers] = useState([]);
  const [groupSummary, setGroupSummary] = useState({
    groupIds: [],
    name: "IO",
    color: "",
  });
  const [form, setForm] = useState({
    arSortCode: "",
    arAccountNumber: "",
    arAccountHolder: "",
    emailCommissionStatementsTo: "",
    emailCommissionStatementsCc: "",
  });

  useEffect(() => {
    if (!adviserKey) return undefined;
    return subscribeAdviserFullDetailsByEmailOrId(adviserKey, setDetails);
  }, [adviserKey]);

  const adviserEmail = useMemo(
    () =>
      details?.adviserDoc?.email ||
      details?.email ||
      state?.adviserEmail ||
      adviserKey,
    [adviserKey, details, state?.adviserEmail],
  );
  const statementRecipientOptions = useMemo(() => {
    const options = new Map([
      [
        DEFAULT_STATEMENT_EMAIL,
        {
          value: DEFAULT_STATEMENT_EMAIL,
          label: DEFAULT_STATEMENT_EMAIL,
        },
      ],
    ]);

    groupAdvisers.forEach((member) => {
      const email = String(member?.email || "").trim();
      if (!email) return;
      const name = [
        member.personal?.preferredName ||
          member.personal?.forename ||
          member.personal?.firstName ||
          member.adviser?.name ||
          member.adviser?.displayName,
        member.personal?.surname ||
          member.personal?.lastName ||
          member.adviser?.surname ||
          member.adviser?.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
      options.set(email, {
        value: email,
        label: name ? `${name} | ${email}` : email,
      });
    });

    statementRecipientEmails(form.emailCommissionStatementsTo).forEach(
      (email) => {
        if (!options.has(email)) {
          options.set(email, { value: email, label: email });
        }
      },
    );

    return [...options.values()];
  }, [form.emailCommissionStatementsTo, groupAdvisers]);

  useEffect(() => {
    if (!adviserEmail || adviserEmail === "-") return undefined;

    let active = true;

    const loadGroup = async () => {
      try {
        setGroupLoading(true);
        setError("");

        const currentIOSnapshot = await getDoc(getIODetailsRef(adviserEmail));
        const currentIODetails = currentIOSnapshot.exists()
          ? currentIOSnapshot.data()
          : {};
        const currentGroupIds = toIdList(currentIODetails.groupId);

        if (!currentGroupIds.length) {
          throw new Error("This adviser does not have an IO Group ID saved.");
        }

        const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));
        const members = await Promise.all(
          adviserSnapshot.docs.map(async (adviserDocument) => {
            const adviser = adviserDocument.data() || {};
            const email = adviser.email || adviserDocument.id;
            const [
              ioSnapshot,
              applicationSnapshot,
              personalSnapshot,
              ratesSnapshot,
            ] =
              await Promise.all([
                getDoc(getIODetailsRef(email)),
                getDoc(
                  doc(
                    db,
                    ...ADVISERS_PATH,
                    adviserDocument.id,
                    "Application Details",
                    "Data",
                  ),
                ),
                getDoc(
                  doc(
                    db,
                    ...ADVISERS_PATH,
                    adviserDocument.id,
                    "Personal Details",
                    "Data",
                  ),
                ),
                getDoc(getAdviserRatesRef(adviserDocument.id)),
              ]);
            const ioDetails = ioSnapshot.exists() ? ioSnapshot.data() : {};
            const application = applicationSnapshot.exists()
              ? applicationSnapshot.data()
              : {};
            const personal = personalSnapshot.exists()
              ? personalSnapshot.data()
              : {};
            const rates = ratesSnapshot.exists() ? ratesSnapshot.data() : {};

            return {
              adviserId: adviserDocument.id,
              adviser,
              application,
              personal,
              email,
              groupIds: toIdList(ioDetails.groupId),
              rates,
            };
          }),
        );
        const groupMembers = members.filter((member) =>
          hasSharedGroupId(currentGroupIds, member.groupIds),
        );

        if (!groupMembers.length) {
          throw new Error("No advisers were found for this IO group.");
        }

        const representative =
          groupMembers.find((member) => member.email === adviserEmail) ||
          groupMembers[0];
        const savedGroup = await getSavedGroupDetails(currentGroupIds[0]);
        const savedBankDetails = savedGroup?.bankDetails || {};
        const savedRates =
          groupMembers.find(
            (member) =>
              member.rates?.arSortCode ||
              member.rates?.arAccountNumber ||
              member.rates?.arAccountHolder,
          )?.rates || representative.rates || {};
        if (!active) return;

        setGroupAdvisers(groupMembers);
        setGroupSummary({
          groupIds: currentGroupIds,
          name:
            savedGroup?.groupName || savedGroup?.group?.GroupName || "IO",
          color:
            representative.adviser.PrimaryColor ||
            representative.adviser.primaryColor ||
            "",
        });
        setForm({
          arSortCode:
            savedBankDetails.arSortCode || savedRates.arSortCode || "",
          arAccountNumber:
            savedBankDetails.arAccountNumber ||
            savedRates.arAccountNumber ||
            "",
          arAccountHolder:
            savedBankDetails.arAccountHolder ||
            savedRates.arAccountHolder ||
            "",
          emailCommissionStatementsTo: normaliseStatementEmails(
            savedBankDetails.emailCommissionStatementsTo,
          ),
          emailCommissionStatementsCc: normaliseStatementEmails(
            savedBankDetails.emailCommissionStatementsCc,
          ),
        });
      } catch (loadError) {
        console.error("Failed to load group bank details:", loadError);
        if (active) {
          setError(loadError.message || "Group bank details could not be loaded.");
          setGroupAdvisers([]);
        }
      } finally {
        if (active) setGroupLoading(false);
      }
    };

    loadGroup();

    return () => {
      active = false;
    };
  }, [adviserEmail]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      validateBankDetails(form);
      if (!isValidStatementEmails(form.emailCommissionStatementsTo)) {
        throw new Error(
          "To email addresses must be valid and separated by commas.",
        );
      }
      if (!isValidStatementEmails(form.emailCommissionStatementsCc)) {
        throw new Error(
          "CC email addresses must be valid and separated by commas.",
        );
      }
      await saveGroupBankDetailsByIOGroupIds(groupSummary.groupIds, form);
      navigate(
        state?.returnTo ||
          `/advisers/${encodeURIComponent(adviserKey || adviserEmail)}?tab=rates`,
        {
          state: {
            activeTab: "rates",
            successMessage:
              "Group bank and statement details saved successfully.",
          },
        },
      );
    } catch (saveError) {
      console.error("Failed to save group bank details:", saveError);
      setError(
        saveError.message || "Failed to save group bank and statement details.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Edit Group Bank &amp; Statement Details</title>
      </Helmet>
      <Sidebar />
      <div className="Main-Container">
        <div className="smFeed">
          <Breadcrumb
            items={[
              { label: "Advisers", to: "/advisers" },
              {
                label: adviserEmail || "Adviser",
                to: `/advisers/${encodeURIComponent(adviserKey || adviserEmail)}?tab=rates`,
              },
              { label: "Edit Group Bank & Statement Details" },
            ]}
          />

          {error ? (
            <AlertBanner
              type="error"
              message={error}
              onClose={() => setError("")}
            />
          ) : null}

          <div className="smEditActions">
            <div className="smEditActionsLeft">
              <div>
                <h3 className="FP_H3">Edit Group Bank & Statement Details</h3>
                <p className="Content-Small muted">
                  These details apply to all advisers in this IO group.
                </p>
              </div>
            </div>
            <PrimaryButton
              label={saving ? "Saving..." : "Save Details"}
              onClick={handleSave}
              disabled={saving || groupLoading || groupAdvisers.length === 0}
            />
          </div>

          <div className="smRatesEditLayout">
            <PostCard title="Group Bank & Statement Details">
              <div className="smGrid2">
                <Field
                  label="Sort Code"
                  type="text"
                  value={form.arSortCode}
                  placeholder="00-00-00"
                  maxLength={8}
                  pattern="\\d{2}-\\d{2}-\\d{2}"
                  inputMode="numeric"
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      arSortCode: formatSortCode(value),
                    }))
                  }
                />
                <Field
                  label="Account Number"
                  type="text"
                  value={form.arAccountNumber}
                  placeholder="xxxxxxxx"
                  maxLength={8}
                  pattern="\\d{8}"
                  inputMode="numeric"
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      arAccountNumber: formatAccountNumber(value),
                    }))
                  }
                />
                <Field
                  label="Account Holder"
                  type="text"
                  value={form.arAccountHolder}
                  placeholder="Account Name"
                  full
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      arAccountHolder: value,
                    }))
                  }
                />
                <div className="smFieldFull">
                  <div className="FP_Body_Semibold">Statement Details</div>
                </div>
                <div className="smFieldFull">
                  <CheckboxMultiSelect
                    label="To"
                    options={statementRecipientOptions}
                    value={statementRecipientEmails(
                      form.emailCommissionStatementsTo,
                    )}
                    placeholder="Select recipients"
                    helperText="Select one or more recipients"
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        emailCommissionStatementsTo:
                          normaliseStatementEmails(value),
                      }))
                    }
                  />
                </div>
                <Field
                  label="CC"
                  type="text"
                  value={form.emailCommissionStatementsCc}
                  placeholder="Email addresses separated by commas"
                  full
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      emailCommissionStatementsCc: value,
                    }))
                  }
                />
              </div>
            </PostCard>

            <PostCard title="Groups">
              <div className="smRatesAdviserDetails">
                <div className="smRatesGroupHeader">
                  <span
                    className="smRatesGroupSwatch"
                    style={{ backgroundColor: groupSummary.color || "#081e56" }}
                    aria-hidden="true"
                  />
                  <div>
                    <div className="FP_Body_Semibold">
                      {groupSummary.name}
                    </div>
                    <div className="Content-Small muted">
                      {groupAdvisers.length} adviser
                      {groupAdvisers.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <Row
                  label="Group ID"
                  value={groupSummary.groupIds.join(", ") || "-"}
                />
              </div>
            </PostCard>
          </div>
        </div>
      </div>
    </div>
  );
}
